// Custom hook for authentication backed by the FastAPI /auth endpoints.
import { useState, useCallback } from "react";
import { authService } from "../services/authService";
import { apiClient } from "../services/apiClient";

export interface AuthUser {
  id: number;
  fname: string;
  email: string;
  role: string;
}

interface UseAuthReturn {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  logout: () => void;
}

const readStoredUser = (): AuthUser | null => {
  try {
    const raw = localStorage.getItem("user");
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
};

export const useAuth = (): UseAuthReturn => {
  const [user, setUser] = useState<AuthUser | null>(readStoredUser);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const login = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const data = await authService.login({ email, password });

      const token: string = data.access_token;
      const loggedInUser: AuthUser = data.user;

      localStorage.setItem("accessToken", token);
      localStorage.setItem("user", JSON.stringify(loggedInUser));
      apiClient.setAuthToken(token);

      setUser(loggedInUser);
      return loggedInUser;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    authService.logout();
    apiClient.removeAuthToken();
    setUser(null);
  }, []);

  return {
    user,
    isAuthenticated: !!user && !!localStorage.getItem("accessToken"),
    isLoading,
    login,
    logout,
  };
};

export default useAuth;
